import type { ImputationLine, ImputationSnapshot } from '../schemas/payment.js';
import type { ImputationOrder } from '../schemas/project.js';
import { moneyToNumber } from '../amortization/decimal.js';

export type ImputableInstallment = {
  id: string;
  installmentNumber: number;
  interestAmount: string;
  principalAmount: string;
  paidInterest: string;
  paidPrincipal: string;
  adminFeeAmount: string;
  paidAdminFee: string;
  /** Outstanding mora for this installment, precomputed by the caller (asOf = payment date). */
  moraOutstanding: string;
};

const cents = (m: string): number => Math.round(moneyToNumber(m) * 100);
const money = (c: number): string => (c / 100).toFixed(2);

/**
 * Allocate a payment across installments oldest-first. Within each installment
 * the waterfall is mora → costo administrativo → (interés/capital per `order`).
 * Cent-aligned integer math. Amount beyond total outstanding is `unapplied`.
 */
export function imputePayment(
  installments: readonly ImputableInstallment[],
  amount: string,
  order: ImputationOrder,
): { snapshot: ImputationSnapshot; appliedTotal: string } {
  let remaining = cents(amount);
  const ordered = [...installments].sort((a, b) => a.installmentNumber - b.installmentNumber);
  const lines: ImputationLine[] = [];

  const take = (cap: number): number => {
    const a = Math.min(remaining, Math.max(0, cap));
    remaining -= a;
    return a;
  };

  for (const it of ordered) {
    if (remaining <= 0) break;
    // Clamp each outstanding amount at 0: a data anomaly (e.g. paid > charged)
    // must never produce a negative cap that `take` would otherwise read as 0 silently.
    const outMora = Math.max(0, cents(it.moraOutstanding));
    const outAdmin = Math.max(0, cents(it.adminFeeAmount) - cents(it.paidAdminFee));
    const outInterest = Math.max(0, cents(it.interestAmount) - cents(it.paidInterest));
    const outPrincipal = Math.max(0, cents(it.principalAmount) - cents(it.paidPrincipal));

    const toMora = take(outMora);
    const toAdmin = take(outAdmin);
    let toInterest = 0;
    let toPrincipal = 0;
    if (order === 'interest_first') {
      toInterest = take(outInterest);
      toPrincipal = take(outPrincipal);
    } else {
      toPrincipal = take(outPrincipal);
      toInterest = take(outInterest);
    }

    if (toMora > 0 || toAdmin > 0 || toInterest > 0 || toPrincipal > 0) {
      lines.push({
        installmentId: it.id,
        appliedToMora: money(toMora),
        appliedToAdminFee: money(toAdmin),
        appliedToInterest: money(toInterest),
        appliedToPrincipal: money(toPrincipal),
      });
    }
  }

  const appliedTotal = cents(amount) - remaining;
  return {
    snapshot: { order, unapplied: money(remaining), lines },
    appliedTotal: money(appliedTotal),
  };
}
